<template>
  <div class="d-flex blabs-list h-100">
    <ion-card class="blab-card m-0 clickable-item" @click="openBlabDetails(post.id)">
      <div class="inside h-100 d-flex flex-column">
        <div class="d-flex">
          <img
            v-image
            class="img"
            :src="resizeUpload(get(post.character, 'info.cropProfilePicture', '/empty.png'), '80x80')"
          />

          <div class="ml-2 flex-grow-1">
            <div class="mt-1 title text-black d-flex align-items-center justify-content-start">
              {{ post.title }}
            </div>

            <div class="d-flex mt-1">
              <small v-if="!post.character" @click.stop>
                <router-link
                  :to="{
                    name: 'profile',
                    params: { username: get(post, 'user.username') },
                  }"
                  >{{ get(post, 'user.username') }}
                </router-link>
              </small>
              <small v-else @click.stop>
                <router-link
                  :to="{
                    name: 'character-profile-new',
                    params: { slug: post.character.slug },
                  }"
                  >{{ get(post.character, 'info.name') }}
                </router-link>
              </small>

              <small class="ml-2">{{ formatTimeAgo(post.created) }}</small>
              <small v-if="post.location" class="ml-2"> <i class="ti-location-pin" /> {{ post.location }}</small>
            </div>
          </div>
        </div>
        <div v-if="extractImages.length" @click.stop>
          <ClientOnly>
            <swiper
              :slides-per-view="1"
              :modules="[SwiperNavigation, SwiperPagination]"
              navigation
              :pagination="{ clickable: true }"
              class="visuals mt-3 w-100"
            >
              <swiper-slide v-for="(visual, index) in extractImages" :key="`vis-${index}`">
                <div class="d-flex justify-content-center">
                  <img v-image :src="resizeUpload(visual, '380h')" @click="openBlabDetails(post.id)" />
                </div>
              </swiper-slide>
              ...
            </swiper>
          </ClientOnly>
        </div>
        <div v-else class="mt-3 ml-1 overflow-text" v-html="truncateText(stripHtmlTags(post.description))" />

        <div class="mt-auto d-flex justify-content-between align-items-center">
          <div class="actions pl-0 mb-n1 d-flex">
            <a href="#" @click.stop>
              <blab-reaction
                :reactions="reactions"
                :user-reaction="userReaction"
                :left-indent-popup="!indentReactionRight"
                :right-indent-popup="indentReactionRight"
                class="rxn"
                @changed="(reaction) => reactionChanged(reaction.key, post, reaction.isInstant)"
              >
                <ion-button
                  color="transparent"
                  class="inline-button icon-button clickable text-primary"
                  :class="{
                    'text-secondary': !!userReaction && userReaction.reaction,
                  }"
                >
                  <div class="d-flex align-items-center justify-content-center">
                    <i class="ti-thumb-up mr-2 icon" />
                    <span class="reaction-count">{{ reactions.total_count || 0 }}</span>
                  </div>
                </ion-button>
              </blab-reaction>
            </a>
          </div>

          <span class="ml-auto mt-1 mr-2 reply">{{ repliesCount }} repl{{ repliesCount === 1 ? 'y' : 'ies' }}</span>
        </div>
      </div>
    </ion-card>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';

import { Prop } from 'vue-property-decorator';
import BlabDetailsModal from '@/modals/BlabDetailsModal.vue';
import { formatTimeAgo } from '@/utils/dateTime';
import { stripHtmlTags } from '@/utils/string';
import BlabReaction from '@/shared-components/BlabReaction.vue';
import store from '~/store';
import { authStore } from '~/pinia-store/auth';

@Options({
  name: 'BlabCard',
  components: { BlabReaction, BlabDetailsModal },
})
export default class BlabCard extends Vue {
  @Prop({ default: () => ({}) }) post!: any;
  @Prop({ default: () => ({}) }) repliesCount!: any;
  @Prop({ default: false }) indentReactionRight!: boolean;

  public first = first;
  public openBlabAsModal = false;
  public blabid = '';
  get = get;
  formatTimeAgo = formatTimeAgo;
  stripHtmlTags = stripHtmlTags;

  public get user() {
    const { user } = authStore();
    return user.value;
  }

  public slideOptions = {
    initialSlide: 1,
    speed: 400,
  };

  setup() {
    const slideOpts = {
      initialSlide: 1,
      speed: 400,
    };
    return { slideOpts };
  }

  public get extractImages() {
    let imgSrc;
      const imgUrls = [];
      const rex = /<img[^>]+src="(https:\/\/[^">]+)"/g;

    while ((imgSrc = rex.exec(this.post.description))) {
      imgUrls.push(imgSrc[1]);
    }

    return imgUrls;
  }

  public showBlabs() {
    const router = useRouter();
    router.push({ name: 'post-page' });
  }

  public get userReaction() {
    if (this.post.user_reaction) {
      return this.post.user_reaction;
    }
  }

  public get visuals() {
    return this.post.visuals;
  }

  public async reactionChanged(reaction: string, post: any, isInstant = false) {
    const reactionResp = await store.dispatch('BlabsModule/react', {
      reaction,
      blab: post,
      isInstant,
    });
    this.$emit('afterReaction', reactionResp);
  }

  public get reactions() {
    const { reaction_counts } = this.post || {};
    return reaction_counts;
  }

  public openBlabDetails(id: string) {
    this.$emit('open', id);
  }

  public truncateText(value: string, length = 120) {
    return truncate(value, { length, separator: /,? +/ });
  }

  mounted() {}
}
</script>

<style lang="sass" scoped>
.inside
  padding: 7px
.visuals
  height: 300px
  @media (max-width: 480px)
    height: 200px
.title
  color: #214163
  font-size: 18px
  font-weight: bold
  overflow-wrap: anywhere
@media(max-width: 419px)
  .title.title
    font-size: 16px
.description
  font-size: 14px
.icon
  font-size: 16px
  font-weight: bold
  margin-bottom: 3px
.reply
  font-size: 12px
.img
  min-width: 50px
  width: 50px !important
  height: 50px !important
  border-radius: 30px
  border: solid gray 0.1px
  object-fit: cover

.visual
  width: 527px !important
  height: 327px !important
  object-fit: cover

.actions
  max-width: 200px
  padding-left: 10px
  *
    font-size: 12px
.reaction-count
  font-size: 14px
  font-weight: bold

.blab-card
  overflow: unset !important
  box-shadow: 1px 1px 5px 0px rgba(33,65,99,0.19) !important

  @media(min-width: 420px)
    min-width: 100% !important
    max-width: 28% !important
  @media(max-width: 419px)
    min-width: 100% !important
    max-width: 50% !important
    .img.img
      width: 25px !important
      height: 25px !important
      min-width: 25px
  .blab-title
    font-size: 20px
    font-weight: bold
.overflow-text
  text-overflow: ellipsis
.rxn
  top: 3px
</style>
